import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/api';

export default function ReportsPage() {
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [resources, setResources] = useState([]);
  const [selectedReleases, setSelectedReleases] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'releaseDate', direction: 'asc' });
  
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    clientId: '',
    mediaType: '',
    mediaResourceId: '',
    statusId: '',
    ownerId: '',
    bookingSource: 'AGENCY',
    materialDueFrom: '',
    materialDueTo: '',
    excludeCancelled: false,
    excludeArchived: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      padding: '0.1rem',
      minWidth: '180px',
      borderColor: '#e2e8f0',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
      color: state.isSelected ? 'white' : '#1e293b',
      '&:active': {
        backgroundColor: '#3b82f6'
      }
    })
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const { excludeCancelled, excludeArchived, ...bookingFilters } = filters;
      const [bookingsRes, clientRes, statusRes, userRes, resourceRes] = await Promise.all([
        api.get('/bookings', { 
          params: { 
            ...bookingFilters, 
            bookingSource: filters.bookingSource === 'All Sources' ? '' : filters.bookingSource,
            limit: 1000,
            includeArchived: !excludeArchived,
            ...(excludeCancelled ? { excludeCancelled: true } : {}),
          } 
        }),
        api.get('/master/clients'),
        api.get('/statuses'),
        api.get('/users'),
        api.get('/master/media-resources'),
      ]);
      
      setBookings(bookingsRes.data.bookings || []);
      setClients(clientRes.data.clients || []);
      setStatuses(statusRes.data.statuses || []);
      setUsers(userRes.data.users || []);
      setResources(resourceRes.data.resources || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateFrom = params.get('dateFrom');
    const dateTo = params.get('dateTo');
    const materialDueFrom = params.get('materialDueFrom');
    const materialDueTo = params.get('materialDueTo');
    const excludeCancelled = params.get('excludeCancelled') === 'true';
    const excludeArchived = params.get('excludeArchived') === 'true';

    if (dateFrom || dateTo || materialDueFrom || materialDueTo || excludeCancelled || excludeArchived) {
      setFilters(prev => ({
        ...prev,
        dateFrom: dateFrom || prev.dateFrom,
        dateTo: dateTo || prev.dateTo,
        materialDueFrom: materialDueFrom || prev.materialDueFrom,
        materialDueTo: materialDueTo || prev.materialDueTo,
        excludeCancelled: excludeCancelled || prev.excludeCancelled,
        excludeArchived: excludeArchived || prev.excludeArchived,
      }));
    }
  }, [location.search]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dateFrom, filters.dateTo, filters.materialDueFrom, filters.materialDueTo, filters.clientId, filters.mediaType, filters.statusId, filters.mediaResourceId, filters.ownerId, filters.bookingSource, filters.excludeCancelled, filters.excludeArchived]);

  const handleFilterChange = (name, value) => {
    // If value is an object (from react-select), get its value property, otherwise use the value directly
    const actualValue = (value && typeof value === 'object' && 'value' in value) ? value.value : value;
    setFilters(prev => ({ ...prev, [name]: actualValue || '' }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const clientOptions = [
    { value: '', label: 'All Clients' },
    ...clients.map(c => ({ value: c.id, label: c.name }))
  ];

  const mediaOptions = [
    { value: '', label: 'All Types' },
    { value: 'PRINT', label: 'Print' },
    { value: 'RADIO', label: 'Radio' },
    { value: 'TV', label: 'TV' },
    { value: 'DIGITAL', label: 'Digital' },
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    ...statuses.map(s => ({ value: s.id, label: s.name }))
  ];

  const publicationOptions = [
    { value: '', label: 'All Publications' },
    ...resources.map(r => ({ value: r.id, label: r.name }))
  ];

  const ownerOptions = [
    { value: '', label: 'All Owners' },
    ...users.map(u => ({ value: u.id, label: u.name || u.email }))
  ];

  const sourceOptions = [
    { value: '', label: 'All Sources' },
    { value: 'AGENCY', label: 'Agency' },
    { value: 'PUBLICATION', label: 'Publication' },
  ];

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      clientId: '',
      mediaType: '',
      mediaResourceId: '',
      statusId: '',
      ownerId: '',
      bookingSource: 'AGENCY',
      materialDueFrom: '',
      materialDueTo: '',
      excludeCancelled: false,
      excludeArchived: false,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const releases = useMemo(() => {
    return bookings.flatMap((booking) =>
      (booking.releaseDates || []).map((release) => ({
        id: `${booking.id}-${release.id}`,
        releaseDate: release.release_date,
        client: booking.clientName,
        media: booking.media_type,
        publication: booking.mediaName || booking.platform_details,
        position: booking.positionName,
        status: release.status_name,
        source: booking.booking_source,
        owner: booking.ownerName,
      }))
    );
  }, [bookings]);

  const summaryStats = useMemo(() => {
    const totals = { PRINT: 0, RADIO: 0, TV: 0, DIGITAL: 0 };
    let totalCount = 0;
    
    bookings.forEach((booking) => {
      const type = booking.media_type?.toUpperCase();
      if (totals[type] !== undefined) {
        totals[type]++;
        totalCount++;
      }
    });

    return Object.entries(totals).map(([label, value]) => ({
      label,
      value,
      percent: totalCount > 0 ? Math.round((value / totalCount) * 100) : 0,
      color: label === 'PRINT' ? '#e9d5ff' : // purple-200
             label === 'RADIO' ? '#cffafe' : // cyan-100
             label === 'TV' ? '#fed7aa' :    // orange-200
             label === 'DIGITAL' ? '#a7f3d0' : // emerald-200 (distinct from print)
             '#f1f5f9'
    }));
  }, [bookings]);

  const getMediaRowStyle = (mediaType) => {
    const type = mediaType?.toUpperCase();
    const colors = {
      PRINT: { backgroundColor: '#f5f3ff' },   // purple-50
      RADIO: { backgroundColor: '#ecfeff' },   // cyan-50
      TV: { backgroundColor: '#fff7ed' },    // orange-50
      DIGITAL: { backgroundColor: '#ecfdf5' }, // emerald-50
    };
    return colors[type] || {};
  };

  const toggleSelect = (id) => {
    setSelectedReleases(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedReleases.length === releases.length) {
      setSelectedReleases([]);
    } else {
      setSelectedReleases(releases.map(r => r.id));
    }
  };

  const exportToExcel = (dataToExport, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(dataToExport.map(r => ({
      'Release Date': formatDate(r.releaseDate),
      'Client': r.client || '',
      'Media Type': r.media || '',
      'Publication': r.publication || '',
      'Position': r.position || '',
      'Source': r.source || '',
      'Status': r.status || '',
      'Owner': r.owner || ''
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Releases");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const handleExportAll = () => {
    if (!releases.length) return alert('No data to export');
    exportToExcel(releases, `All_Releases_${new Date().toISOString().split('T')[0]}`);
  };

  const handleExportSelected = () => {
    if (!selectedReleases.length) return alert('Please select at least one item to export');
    const selectedData = releases.filter(r => selectedReleases.includes(r.id));
    exportToExcel(selectedData, `Selected_Releases_${new Date().toISOString().split('T')[0]}`);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedReleases = useMemo(() => {
    return [...releases].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [releases, sortConfig]);

  const totalPages = Math.ceil(sortedReleases.length / itemsPerPage);
  const currentReleases = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedReleases.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedReleases, currentPage]);

  return (
    <div className="reports-page">
      <header className="reports-header">
        <div>
          <h2>Reports & Export</h2>
          <p>Generate reports and export data</p>
        </div>
        <div className="header-actions">
          <button className="btn-export-selected" onClick={handleExportSelected}>
            <span>📥</span> Export Selected
          </button>
          <button className="btn-export-all" onClick={handleExportAll}>
            <span>📄</span> Export All
          </button>
        </div>
      </header>

      <section className="reports-filters-card">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Date From</label>
            <DatePicker
              selected={filters.dateFrom ? new Date(filters.dateFrom) : null}
              onChange={(date) => {
                const formattedDate = date ? 
                  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : 
                  '';
                handleFilterChange('dateFrom', formattedDate);
              }}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/yyyy"
              className="datepicker-input"
            />
          </div>
          <div className="filter-group">
            <label>Date To</label>
            <DatePicker
              selected={filters.dateTo ? new Date(filters.dateTo) : null}
              onChange={(date) => {
                const formattedDate = date ? 
                  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : 
                  '';
                handleFilterChange('dateTo', formattedDate);
              }}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/yyyy"
              className="datepicker-input"
            />
          </div>
          <div className="filter-group">
            <label>Client</label>
            <Select
              options={clientOptions}
              value={clientOptions.find(opt => opt.value === filters.clientId)}
              onChange={(opt) => handleFilterChange('clientId', opt.value)}
              placeholder="Search Client..."
              styles={customSelectStyles}
              isSearchable
            />
          </div>
          <div className="filter-group">
            <label>Media Type</label>
            <Select
              options={mediaOptions}
              value={mediaOptions.find(opt => opt.value === filters.mediaType)}
              onChange={(opt) => handleFilterChange('mediaType', opt.value)}
              placeholder="Search Type..."
              styles={customSelectStyles}
              isSearchable
            />
          </div>
          <div className="filter-group">
            <label>Status</label>
            <Select
              options={statusOptions}
              value={statusOptions.find(opt => opt.value === filters.statusId)}
              onChange={(opt) => handleFilterChange('statusId', opt ? opt.value : '')}
              placeholder="Search Status..."
              styles={customSelectStyles}
              isSearchable
              isClearable
            />
          </div>
          <div className="filter-group">
            <label>Publication</label>
            <Select
              options={publicationOptions}
              value={publicationOptions.find(opt => opt.value === filters.mediaResourceId)}
              onChange={(opt) => handleFilterChange('mediaResourceId', opt ? opt.value : '')}
              placeholder="Search Publication..."
              styles={customSelectStyles}
              isSearchable
              isClearable
            />
          </div>
          <div className="filter-group">
            <label>Owner</label>
            <Select
              options={ownerOptions}
              value={ownerOptions.find(opt => opt.value === filters.ownerId)}
              onChange={(opt) => handleFilterChange('ownerId', opt ? opt.value : '')}
              placeholder="Search Owner..."
              styles={customSelectStyles}
              isSearchable
              isClearable
            />
          </div>
          <div className="filter-group">
            <label>Source</label>
            <Select
              options={sourceOptions}
              value={sourceOptions.find(opt => opt.value === filters.bookingSource)}
              onChange={(opt) => handleFilterChange('bookingSource', opt ? opt.value : '')}
              placeholder="Search Source..."
              styles={customSelectStyles}
              isSearchable
              isClearable
            />
          </div>
        </div>
        <div className="filter-actions" style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <button className="btn-generate" onClick={fetchData}>Generate Report</button>
          <button className="btn-clear" onClick={clearFilters}>Clear Filters</button>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer', color: '#475569', fontWeight: 500 }}>
            <input 
              type="checkbox" 
              checked={filters.excludeArchived} 
              onChange={(e) => handleFilterChange('excludeArchived', e.target.checked)}
              style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
            />
            Exclude Archived
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer', color: '#475569', fontWeight: 500 }}>
            <input 
              type="checkbox" 
              checked={filters.excludeCancelled} 
              onChange={(e) => handleFilterChange('excludeCancelled', e.target.checked)}
              style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
            />
            Exclude Cancelled
          </label>
        </div>
      </section>

      <section className="summary-stats-section">
        <h3>Summary Statistics</h3>
        <div className="stats-grid">
          {summaryStats.map((stat) => (
            <div 
              key={stat.label} 
              className={`stat-box ${stat.label.toLowerCase()}`}
              style={{ backgroundColor: stat.color, border: 'none' }}
            >
              <span className="stat-box-label">{stat.label} Bookings</span>
              <span className="stat-box-value">{stat.value}</span>
              <span className="stat-box-percent">{stat.percent}% of total</span>
            </div>
          ))}
        </div>
      </section>

      <div className="report-table-card">
        <div className="scroll-container">
          <table className="report-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input 
                    type="checkbox" 
                    checked={releases.length > 0 && selectedReleases.length === releases.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort('releaseDate')} style={{ cursor: 'pointer' }}>
                  Release Date {sortConfig.key === 'releaseDate' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('client')} style={{ cursor: 'pointer' }}>
                  Client {sortConfig.key === 'client' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('media')} style={{ cursor: 'pointer' }}>
                  Media Type {sortConfig.key === 'media' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('publication')} style={{ cursor: 'pointer' }}>
                  Publication {sortConfig.key === 'publication' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('position')} style={{ cursor: 'pointer' }}>
                  Position {sortConfig.key === 'position' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('source')} style={{ cursor: 'pointer' }}>
                  Source {sortConfig.key === 'source' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                  Status {sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('owner')} style={{ cursor: 'pointer' }}>
                  Owner {sortConfig.key === 'owner' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>Loading report data...</td></tr>
              ) : currentReleases.length > 0 ? (
                currentReleases.map((release) => (
                  <tr key={release.id} style={getMediaRowStyle(release.media)}>
                    <td>
                      <input 
                        type="checkbox" 
                        checked={selectedReleases.includes(release.id)} 
                        onChange={() => toggleSelect(release.id)}
                      />
                    </td>
                    <td>{formatDate(release.releaseDate)}</td>
                    <td>{release.client || '—'}</td>
                    <td>{release.media}</td>
                    <td>{release.publication || '—'}</td>
                    <td>{release.position || '—'}</td>
                    <td>
                      <span className={`tag-pill ${release.source?.toLowerCase()}`}>
                        {release.source}
                      </span>
                    </td>
                    <td>
                      <span className={`tag-pill ${release.status?.toLowerCase()}`}>
                        {release.status}
                      </span>
                    </td>
                    <td>{release.owner || '—'}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>No records found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="report-footer">
          <div className="footer-info">
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, releases.length)} of {releases.length} releases
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                if (totalPages <= 7) return true;
                return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
              })
              .map((page, index, array) => (
                <span key={page}>
                  {index > 0 && array[index - 1] !== page - 1 && <span style={{ padding: '0 0.5rem' }}>...</span>}
                  <button
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                </span>
              ))}
            <button
              className="pagination-btn"
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
